// Disable console.log in production (unless on dev server or debug mode enabled)
if (import.meta.env.PROD) {
    // Check if we're on dev server or debug mode is enabled
    const isDevServer = window.location.hostname === 'dev.kentremovalsstorage.com.au';
    const isDebugEnabled = localStorage.getItem('DEBUG_API') === 'true';

    // Only disable console if NOT on dev server AND debug not enabled
    if (!isDevServer && !isDebugEnabled) {
        console.log = function () {};
        console.debug = function () {};
        console.info = function () {};
    }
    // Keep console.warn and console.error for important messages regardless
}

// Livewire + Alpine are imported dynamically (see boot() below) so the ~167KB
// runtime is NOT parsed/evaluated during initial page load on pages that have no
// Livewire component — which is the large majority (homepage, all interstate/SEO
// pages). That import-time eval was the single biggest mobile TBT cost (KENT-811).
//
// Alpine ships *inside* the Livewire bundle, and Alpine drives the header / mobile
// nav, so on non-Livewire pages we still load it on the first user interaction (or
// idle, whichever comes first) — a nav tap always triggers it immediately.

// Import custom utilities
import '@js/utilities/trackingParams';
import '@js/utilities/pageScroll';
import '@js/utilities/moveHubAnalytics';
import '@js/utilities/formSubmissionDataLayer';
import '@js/utilities/smoothScroll';
import '@js/utilities/smoothAnchorScroll';
import '@js/utilities/scrollAnimation';

// Alpine plugins — small, safe to import statically; registered after Alpine loads.
import collapse from '@alpinejs/collapse';
import Precognition from 'laravel-precognition-alpine';
import focus from '@alpinejs/focus';
import intersect from '@alpinejs/intersect';
import anchor from '@alpinejs/anchor';
import mask from '@alpinejs/mask';
import Mask from '@ryangjchandler/alpine-mask';

// Alpine component registry.
//
// Each entry is a dynamic import() so Vite splits every component into its own
// chunk. Only the components actually present in the page's DOM are fetched and
// registered before Alpine starts — pages no longer ship ~35 components they
// never use (see KENT-811: monolithic site.js was the bulk of mobile TBT).
const componentLoaders = {
    headerNav: () => import('@js/components/headerNav'),
    videoPlayer: () => import('@js/components/videoPlayer'),
    rollingText: () => import('@js/components/rollingText'),
    imagesTextCarousel: () => import('@js/components/imagesTextCarousel'),
    personasCarousel: () => import('@js/components/personasCarousel'),
    movingToCTA: () => import('@js/components/movingToCTA'),
    additionalMoveServices: () => import('@js/components/additionalMoveServices'),
    phoneNumberInput: () => import('@js/components/phoneNumberInput'),
    testimonialsCarouselDesktop: () => import('@js/components/testimonialsCarousel'),
    logosCarousel: () => import('@js/components/logosCarousel'),
    branchLocations: () => import('@js/components/branchLocations'),
    resourceBanner: () => import('@js/components/resourceBanner'),
    resourceCarousel: () => import('@js/components/resourceCarousel'),
    resourceImageCarousel: () => import('@js/components/resourceImageCarousel'),
    processModule: () => import('@js/components/processModule'),
    timelineModule: () => import('@js/components/timelineModule'),
    countUp: () => import('@js/components/countUp'),
    formSubmission: () => import('@js/components/formSubmission'),
    customSelect: () => import('@js/components/customSelect'),
    fullPageForm: () => import('@js/components/fullPageForm'),
    movingQuote: () => import('@js/components/movingQuote'),
    placeAutocomplete: () => import('@js/components/placeAutocomplete'),
    datePicker: () => import('@js/components/datePicker'),
    modal: () => import('@js/components/modal'),
    multiStepsForm: () => import('@js/components/multiStepsForm'),
    quoteSelectorCarousel: () => import('@js/components/quoteSelectorCarousel'),
    featureCardsCarousel: () => import('@js/components/featureCardsCarousel'),
    journeyModuleCarousel: () => import('@js/components/journeyModuleCarousel'),
    twoCardTabbedModule: () => import('@js/components/twoCardTabbedModule'),
    animatedText: () => import('@js/components/animatedText'),
    animatedButton: () => import('@js/components/animatedButton'),
    animatedIcon: () => import('@js/components/animatedIcon'),
    animatedModule: () => import('@js/components/animatedModule'),
    corporateTestimonials: () => import('@js/components/corporateTestimonials'),
    addToCalendar: () => import('@js/components/addToCalendar'),
    verifyQuote: () => import('@js/components/verifyQuote'),
    moveHubWalkthrough: () => import('@js/components/moveHubWalkthrough'),
};

// Detect which registered components are referenced anywhere in the page.
//
// We match against the full serialized markup (document.documentElement.outerHTML)
// rather than querySelectorAll('[x-data]'), because querySelectorAll does NOT
// descend into <template> content — and many components (multi-step form fields,
// moving-quote steps, the storage-locations modal) only appear inside
// <template x-if/x-for> blocks. Those templates are still present in the initial
// server-rendered HTML, so a string scan finds them; an element scan would miss
// them and Alpine would throw "X is not defined" when the template renders.
//
// A single pre-built alternation regex keeps this O(names) instead of recompiling
// per element — this runs synchronously before Livewire.start(), inside the TBT
// window KENT-811 is trying to shrink.
const componentNames = Object.keys(componentLoaders);
const componentNameRe = new RegExp(`\\b(${componentNames.join('|')})\\b`, 'g');

function findUsedComponents() {
    const used = new Set();
    const markup = document.documentElement.outerHTML;

    let match;
    componentNameRe.lastIndex = 0;
    while ((match = componentNameRe.exec(markup)) !== null) {
        used.add(match[1]);
    }

    return [...used];
}

async function registerUsedComponents(Alpine) {
    const used = findUsedComponents();

    await Promise.all(
        used.map(async (name) => {
            try {
                const module = await componentLoaders[name]();
                Alpine.data(name, module.default);
            } catch (error) {
                console.error(`Failed to load Alpine component "${name}"`, error);
            }
        })
    );
}

// Boot: dynamically import the Livewire+Alpine runtime, register plugins +
// the in-page components, then start. Splitting this import out of the static
// graph means the ~167KB runtime is only parsed when boot() actually runs —
// immediately on Livewire pages, deferred on the rest (see scheduleBoot).
let booted = false;

// Fast month prev/next (and timezone changes) on the booking-availability-picker
// each trigger a Livewire round-trip that runs Graph-backed availability lookups.
// Livewire queues them, so a rapid series fires one request per click and spams
// the server. Abort the request still in flight whenever a newer booking-picker
// request starts, collapsing a burst of clicks down to just the latest one.
function registerBookingRequestCancellation(Livewire) {
    let inFlight = null;

    Livewire.hook('request', ({ options, payload, respond, fail }) => {
        const targetsBookingPicker = (payload?.components ?? []).some((component) => {
            try {
                return JSON.parse(component.snapshot)?.memo?.name === 'booking-availability-picker';
            } catch {
                return false;
            }
        });

        if (!targetsBookingPicker) return;

        // Supersede the previous, still-pending request.
        if (inFlight) inFlight.abort();

        const controller = new AbortController();
        inFlight = controller;
        options.signal = controller.signal;

        const clear = () => {
            if (inFlight === controller) inFlight = null;
        };

        respond(clear);
        fail(({ preventDefault }) => {
            clear();
            // A superseded request rejects with an AbortError — swallow it so
            // Livewire doesn't surface an aborted commit as a real failure.
            if (controller.signal.aborted) preventDefault();
        });
    });
}

async function boot() {
    if (booted) return;
    booted = true;

    const { Livewire, Alpine } = await import('@public/vendor/livewire/livewire.esm');

    // Listens for toasts dispatched by the Masmerise\Toaster\Toastable trait
    // (server-side `$this->success()`/`->error()`/etc.) - only meaningful on
    // pages with a Livewire component, hence loaded alongside Livewire here
    // rather than as a static top-level import (see the perf note above).
    await import('../../vendor/masmerise/livewire-toaster/resources/js');

    Alpine.plugin(collapse);
    Alpine.plugin(focus);
    Alpine.plugin(intersect);
    Alpine.plugin(anchor);
    Alpine.plugin(Precognition);
    Alpine.plugin(mask);
    Alpine.plugin(Mask);

    try {
        await registerUsedComponents(Alpine);
    } catch (error) {
        console.error('Component registration failed', error);
    }

    registerBookingRequestCancellation(Livewire);

    // Livewire.start() also starts Alpine.
    Livewire.start();
}

// A page "needs Livewire now" if the server rendered a Livewire component into it.
// Those pages must hydrate immediately. Every other page (homepage, interstate /
// SEO / service pages — the bulk) defers the runtime to first interaction or idle,
// whichever fires first, so a header / mobile-nav tap always boots it instantly.
function scheduleBoot() {
    if (document.querySelector('[wire\\:id]')) {
        boot();
        return;
    }

    // Boot on first interaction so a header/nav tap is instant...
    const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
    const trigger = () => {
        events.forEach((e) => window.removeEventListener(e, trigger));
        boot();
    };
    events.forEach((e) => window.addEventListener(e, trigger, { once: true, passive: true }));

    // ...otherwise boot right after the load event. We deliberately do NOT use
    // requestIdleCallback here: the Voiceflow chat widget also injects on idle and
    // measures the layout to position itself, and our ~167KB Livewire+Alpine parse
    // in the same idle slot janks the main thread, making Voiceflow mis-position
    // (it rendered top-left instead of by its toggle). Booting on `load` keeps the
    // runtime off the initial render path while leaving the idle window free for
    // the chat widget.
    const bootAfterLoad = () => setTimeout(boot, 200);
    if (document.readyState === 'complete') {
        bootAfterLoad();
    } else {
        window.addEventListener('load', bootAfterLoad, { once: true });
    }
}

scheduleBoot();
